import React from 'react';
import { Button, Dialog, Modal } from 'alurkerja-ui';
import { CreateButtonModal } from '@/components/shared/button/CreateButton';
import { FormContainer, FormFooter } from '@/components/form/FormContainer';
import { useForm } from 'react-hook-form';
import { InputField } from '@/components/form/fields/InputField';
import { moduleSchema, IModuleSchema, ModuleService } from '@/api/module';
import { zodResolver } from '@hookform/resolvers/zod';
import { IProjectSchema } from '@/api/project';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import { SelectField } from '@/components/form/fields/SelectField';
import { UserService } from '@/api/user';
import { IUserSchema } from '@/api/user';

type CreateModuleProps = {
  project: IProjectSchema;
  onSuccess: () => void;
};

interface ModalContentProps extends CreateModuleProps {
  closeModal?: () => void;
}

const ModalContent: React.FC<ModalContentProps> = ({
  closeModal,
  project,
  onSuccess,
}) => {
  const form = useForm<IModuleSchema>({
    resolver: zodResolver(moduleSchema),
  });
  
  const { data: usersData } = UserService.useGetAllUser();
  
  const userOptions =
    usersData?.data?.content?.map((user: IUserSchema) => ({
      label: `${user.first_name} ${user.last_name}`,
      value: user.id,
    })) ?? [];
  
  const { mutate, isPending: isLoading } = ModuleService.useCreate();

  const submitAction = (data: IModuleSchema) => {
    mutate(
      {
        ...data,
        project_id: project.id,
      },
      {
        onSuccess: () => {
          Dialog.success({
            title: 'Success',
            description: 'Module has been created',
            callback: () => {
              onSuccess();

              closeModal?.();
            },
          });
        },
      },
    );
  };

  return (
    <FormContainer form={form} onSubmit={submitAction}>
      <InputField label="Name" name="module_name" />

      <SelectField
        label="PIC"
        name="pic_id"
        options={userOptions}
        placeholder="Select PIC"
      />

      <FormFooter>
        <OutlineButton onClick={closeModal}>Cancel</OutlineButton>

        <Button loading={isLoading}>Save</Button>
      </FormFooter>
    </FormContainer>
  );
};

export const CreateModule: React.FC<CreateModuleProps> = props => {
  return (
    <Modal title="Add Epic" triggerButton={<CreateButtonModal />}>
      {({ closeModal }) => {
        return (
          <div className="p-4">
            <ModalContent closeModal={closeModal} {...props} />
          </div>
        );
      }}
    </Modal>
  );
};
